/**
 * AuditTrailTab — Real-Time "God View" of the Argus Xenia ecosystem.
 *
 * Features:
 *  - System Health Score circular dial (24h window)
 *  - Live/Pause toggle with queued event counter
 *  - Golden Spoon Demo Mode — swaps real data for fictional restaurant persona
 *    + ROI ticker showing accumulated savings during the demo
 *  - AI Reasoning Tooltips — deterministic logic summary per event
 *  - Confidence Dots 🟢 Deterministic / 🟡 Rule-based / 🔵 User Action
 *  - Processing time per decision
 *  - Filterable timeline by event_type and date range
 *  - "Simulate Plating Issue" demo button — triggers full Apollo→Iris→Atlas chain
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Activity, Shield, Settings, Wrench, FileText, TrendingUp,
  AlertTriangle, RefreshCw, Play, Pause, Star, EyeOff,
  Zap, ChevronDown, ChevronUp, Filter, HelpCircle, Clock, Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Golden Spoon Demo Persona ─────────────────────────────────────────

const GS = {
  name:       "The Golden Spoon",
  phone:      "+1 (212) 555-0142",
  email:      "chef@goldenspoondemo.com",
  cuisine:    "Contemporary French",
  address:    "42 Park Avenue, New York, NY",
  sop_titles: ["Lobster Bisque", "Duck Confit", "Tarte Tatin", "Foie Gras Terrine", "Crème Brûlée"],
  guest_names:["Guest #4821", "Guest #7193", "Guest #2847", "Guest #5519", "Guest #3302"],
  staff_names:["Chef Marco", "Server Sofia", "Manager Alex", "Line Cook James", "Host Emma"],
};

const DEMO_ROI = {
  iris_task:             10,  // plating issue caught → $10 waste/comping saved
  supervisor_event:       5,  // escalation handled by AI → $5 recovery saved
  supervisor_escalation:  5,
  sop_change:            15,  // SOP created → $15 training value
  config_change:          2,  // admin efficiency
  migration_run:          0,
};

function _dhash(str, len) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) { h = ((h << 5) - h) + str.charCodeAt(i); h |= 0; }
  return Math.abs(h) % len;
}

// ── Demo Report Generator ─────────────────────────────────────────────

function generateDemoReport({ health, roiTotal, roiEventCount, entries }) {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // Supervisor activity counts
  const sc = { echo: 0, apollo: 0, iris: 0 };
  const ss = { echo: 0, apollo: 0, iris: 0 };
  entries.forEach(e => {
    const type = e.event_type || e.type;
    const by   = (e.triggered_by || "").toLowerCase();
    if (by.includes("echo") || type === "supervisor_escalation") { sc.echo++;   ss.echo   += 5;  }
    if (type === "iris_task")                                     { sc.iris++;   ss.iris   += 10; }
    if (by.includes("apollo") && type !== "iris_task")           { sc.apollo++; ss.apollo += 10; }
  });

  const top5 = entries.slice(0, 5);
  const BADGE_STYLE = {
    config_change:         "background:#dbeafe;color:#1d4ed8",
    maintenance_toggle:    "background:#fef3c7;color:#92400e",
    supervisor_escalation: "background:#fee2e2;color:#991b1b",
    sop_change:            "background:#ede9fe;color:#5b21b6",
    iris_task:             "background:#fef9c3;color:#92400e",
    supervisor_event:      "background:#f3f4f6;color:#374151",
  };
  const CONF_LABEL = {
    deterministic: "🟢 Deterministic",
    rule_based:    "🟡 Rule-Based",
    user_action:   "🔵 User Action",
  };

  const eventsHtml = top5.map(e => {
    const logic  = generateLogicSummary(e);
    const cfg    = getEventConfig(e.event_type || e.type);
    const style  = BADGE_STYLE[e.event_type || e.type] || BADGE_STYLE.supervisor_event;
    const conf   = CONF_LABEL[logic.confidence] || "🟡 Rule-Based";
    const action = swapGoldenSpoon(logic.summary, true);
    return `<li style="display:flex;align-items:flex-start;gap:12px;padding:12px 0;border-bottom:1px solid #f3f4f6">
      <span style="font-size:10px;font-weight:700;padding:3px 8px;border-radius:100px;white-space:nowrap;flex-shrink:0;${style}">${cfg.label}</span>
      <div>
        <div style="font-size:12px;color:#374151;line-height:1.5">${action}</div>
        <div style="font-size:10px;color:#9ca3af;margin-top:3px">${conf} · ${logic.time}</div>
      </div></li>`;
  }).join("");

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>${GS.name} — Argus Xenia Demo Report</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Inter',sans-serif;background:#fff;color:#1a1a2e}
.page{max-width:720px;margin:0 auto}
.header{background:#040F49;color:#fff;padding:32px 40px 24px}
.hdr-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px}
.logo-row{display:flex;align-items:center;gap:14px}
.rname{font-size:24px;font-weight:900;color:#D4AF37;letter-spacing:-0.02em}
.rtag{font-size:11px;color:rgba(212,175,55,0.55);margin-top:2px;letter-spacing:0.12em;text-transform:uppercase}
.rmeta{text-align:right}
.rtitle{font-size:10px;color:rgba(255,255,255,0.45);text-transform:uppercase;letter-spacing:0.2em}
.rdate{font-size:13px;color:rgba(255,255,255,0.8);margin-top:4px}
.goldline{height:2px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0.1))}
.metrics{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #e5e7eb}
.mb{padding:24px 40px}.mb:first-child{border-right:1px solid #e5e7eb}
.mlabel{font-size:10px;text-transform:uppercase;letter-spacing:0.2em;color:#9ca3af;margin-bottom:8px;font-weight:700}
.mval{font-size:40px;font-weight:900;line-height:1}
.msub{font-size:11px;color:#9ca3af;margin-top:6px}
.section{padding:22px 40px;border-bottom:1px solid #e5e7eb}
.stitle{font-size:10px;text-transform:uppercase;letter-spacing:0.25em;color:#9ca3af;margin-bottom:14px;font-weight:700}
.supgrid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
.scard{background:#f9fafb;border-radius:10px;padding:16px;text-align:center;border:1px solid #e5e7eb}
.sname{font-size:13px;font-weight:800}
.srole{font-size:10px;color:#9ca3af;margin-top:2px}
.scount{font-size:26px;font-weight:900;margin:8px 0 2px}
.ssave{font-size:11px;color:#9ca3af}
.elist{list-style:none}
.footer{padding:18px 40px;background:#f9fafb;display:flex;justify-content:space-between;align-items:center}
.fl{font-size:10px;color:#9ca3af}.fr{font-size:10px;color:#D4AF37;font-weight:700}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body><div class="page">
<div class="header">
  <div class="hdr-top">
    <div class="logo-row">
      <svg width="44" height="44" viewBox="0 0 80 80" fill="none">
        <path d="M40 6L12 20V42C12 58.5 24.5 73.5 40 78C55.5 73.5 68 58.5 68 42V20L40 6Z" stroke="#D4AF37" stroke-width="2.5" fill="rgba(212,175,55,0.08)"/>
        <path d="M24 42C24 42 31 32 40 32C49 32 56 42 56 42C56 42 49 52 40 52C31 52 24 42 24 42Z" stroke="#00FF41" stroke-width="1.5" fill="none"/>
        <circle cx="40" cy="42" r="4" fill="#00FF41"/>
      </svg>
      <div><div class="rname">${GS.name}</div><div class="rtag">Argus Xenia Intelligence Report</div></div>
    </div>
    <div class="rmeta"><div class="rtitle">Demo Session Report</div><div class="rdate">${date}</div></div>
  </div>
  <div class="goldline"></div>
</div>
<div class="metrics">
  <div class="mb">
    <div class="mlabel">System Health</div>
    <div class="mval" style="color:${health?.color || "#00FF41"}">${health?.score ?? 100}</div>
    <div class="msub">${health?.label || "Excellent"} · ${health?.critical_events_24h ?? 0} critical in 24h</div>
  </div>
  <div class="mb">
    <div class="mlabel">Total Demo Savings</div>
    <div class="mval" style="color:#D4AF37">$${roiTotal.toLocaleString()}</div>
    <div class="msub">${roiEventCount} events handled · $5–15 per event</div>
  </div>
</div>
<div class="section">
  <div class="stitle">Supervisor Activity Summary</div>
  <div class="supgrid">
    <div class="scard">
      <div class="sname" style="color:#3b82f6">ECHO</div>
      <div class="srole">The Voice — AI Receptionist</div>
      <div class="scount" style="color:#3b82f6">${sc.echo}</div>
      <div class="ssave">${sc.echo > 0 ? "$" + ss.echo + " recovered" : "On standby"}</div>
    </div>
    <div class="scard">
      <div class="sname" style="color:#8b5cf6">APOLLO</div>
      <div class="srole">The Eyes — Vision AI</div>
      <div class="scount" style="color:#8b5cf6">${sc.apollo + sc.iris}</div>
      <div class="ssave">${(sc.apollo + sc.iris) > 0 ? "$" + (ss.apollo + ss.iris) + " waste prevented" : "On standby"}</div>
    </div>
    <div class="scard">
      <div class="sname" style="color:#D4AF37">IRIS</div>
      <div class="srole">The Messenger — Router</div>
      <div class="scount" style="color:#D4AF37">${sc.iris}</div>
      <div class="ssave">${sc.iris > 0 ? "Instant delivery · < 1s" : "On standby"}</div>
    </div>
  </div>
</div>
<div class="section">
  <div class="stitle">Top Events Handled This Session</div>
  <ul class="elist">${eventsHtml || '<li style="padding:16px 0;color:#9ca3af;font-size:13px">No events recorded this session. Run a simulation to populate.</li>'}</ul>
</div>
<div class="footer">
  <div class="fl">Confidential · Generated by Argus Xenia Intelligence Suite<br>This report was produced from a live AI demo session.</div>
  <div class="fr">ARGUS-XENIA-CORE-V1</div>
</div>
</div></body></html>`;

  const win = window.open("", "_blank", "width=820,height=960");
  if (!win) { alert("Please allow popups to download the report."); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 700);
}

function swapGoldenSpoon(text, enabled) {
  if (!enabled || !text) return String(text ?? "");
  let s = String(text);
  s = s.replace(/argus xenia/gi, GS.name);
  s = s.replace(/self-driving restaurant/gi, GS.name);
  s = s.replace(/\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, GS.phone);
  s = s.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, GS.email);
  s = s.replace(/(Guest|Owner|Customer):\s*([A-Z][a-zA-Z]+(?:\s[A-Z][a-zA-Z]+)?)/g,
    (_, label, name) => `${label}: ${GS.guest_names[_dhash(name, GS.guest_names.length)]}`);
  s = s.replace(/SOP (?:created|updated):\s+"?([^"\[{\n]+)"?/g,
    (match, title) => match.replace(title.trim(), GS.sop_titles[_dhash(title, GS.sop_titles.length)]));
  return s;
}

function swapContext(ctx, enabled) {
  if (!enabled || !ctx) return ctx;
  const safe = { ...ctx };
  if (safe.phone)         safe.phone         = GS.phone;
  if (safe.email)         safe.email         = GS.email;
  if (safe.title)         safe.title         = GS.sop_titles[_dhash(safe.title, GS.sop_titles.length)];
  if (safe.guest_name)    safe.guest_name    = GS.guest_names[_dhash(safe.guest_name, GS.guest_names.length)];
  if (safe.customer_name) safe.customer_name = GS.guest_names[_dhash(safe.customer_name, GS.guest_names.length)];
  if (safe.summary)       safe.summary       = "[Demo interaction summary]";
  return safe;
}

// ── AI Reasoning — Deterministic Logic Summary ────────────────────────

const CONFIDENCE = {
  deterministic: { color: "#00FF41", dot: "🟢", label: "Deterministic", desc: "Threshold rule — same input always gives same outcome" },
  rule_based:    { color: "#f59e0b", dot: "🟡", label: "Rule-Based",    desc: "Routing logic selected from multiple valid outcomes" },
  user_action:   { color: "#3b82f6", dot: "🔵", label: "User Action",   desc: "A human made this change directly via the UI" },
};

function generateLogicSummary(entry) {
  const ctx   = entry.context || {};
  const type  = entry.event_type || entry.type || "";
  const inner = ctx.context || ctx; // nested context from SharedBrain

  switch (type) {
    case "supervisor_escalation": {
      const score = inner.agitation_score ?? ctx.agitation_score;
      if (score != null) {
        const pct = Math.round(score * 100);
        return {
          summary: `Echo escalated: guest agitation reached ${pct}% — exceeded the 70% threshold. Routed to manager.`,
          confidence: "deterministic",
          rule: "agitation_score ≥ 0.7 → automatic manager escalation",
          time: "< 1s",
        };
      }
      return {
        summary: `Supervisor escalation: ${entry.action || "Critical condition detected"}. Requires human review.`,
        confidence: "deterministic",
        rule: "CRITICAL/HIGH priority events trigger escalation pipeline",
        time: "< 1s",
      };
    }

    case "iris_task": {
      const priority = ctx.priority || entry.priority || "normal";
      const taskType = ctx.task_type || entry.task_type || "task";
      const assigned = ctx.assigned_name || "broadcast to eligible staff";
      const loc      = ctx.location || "kitchen";
      return {
        summary: `Iris created a ${priority}-priority ${taskType.replace("_"," ")} task at ${loc}, assigned to ${assigned}.`,
        confidence: "rule_based",
        rule: "Iris routing: task_type → eligible roles → lowest workload → proximity score",
        time: "< 1s",
      };
    }

    case "supervisor_event": {
      const sup  = entry.supervisor || ctx.supervisor || "supervisor";
      const prio = entry.priority   || ctx.priority   || "normal";
      const agitation = inner.agitation_score;
      if (agitation != null) {
        return {
          summary: `${sup.toUpperCase()} logged a ${prio} event: guest agitation at ${Math.round(agitation*100)}%. Broadcast to connected clients.`,
          confidence: "deterministic",
          rule: `SharedBrain.log_event() → broadcast_manager → all WebSocket subscribers`,
          time: "< 1s",
        };
      }
      return {
        summary: `${sup.toUpperCase()} logged a ${prio}-priority event to SharedBrain. Published to live WebSocket feed.`,
        confidence: prio === "critical" || prio === "high" ? "deterministic" : "rule_based",
        rule: "SharedBrain.log_event() broadcasts to all connected WS clients for this restaurant",
        time: "< 1s",
      };
    }

    case "config_change": {
      const fields = ctx.fields || [];
      const fieldStr = fields.length ? fields.join(", ") : "restaurant settings";
      return {
        summary: `Admin updated ${fieldStr} in the Command Centre. Changes take effect immediately.`,
        confidence: "user_action",
        rule: "Direct user action via Config tab → PATCH /api/config/{id}",
        time: "Immediate",
      };
    }

    case "maintenance_toggle": {
      const isOn = ctx.is_maintenance_mode;
      return {
        summary: `Maintenance mode ${isOn ? "ENABLED" : "DISABLED"}. ${isOn
          ? "Production domain now shows Coming Soon page. Emergent preview unaffected."
          : "Production site fully restored. All supervisors back online."}`,
        confidence: "user_action",
        rule: "DB flag → 30s cache TTL → hostname bypass for preview URL",
        time: "≤ 30s propagation",
      };
    }

    case "sop_change": {
      const action = ctx.action || "modified";
      const dept   = (ctx.department || "boh").toUpperCase();
      const title  = ctx.title || "SOP";
      return {
        summary: `Athena ${action === "create" ? "created" : "updated"} "${title}" for ${dept}. SharedBrain event published for cross-supervisor awareness.`,
        confidence: "user_action",
        rule: "SOP write → Athena logs to supervisor_events + admin_audit_log",
        time: "< 1s",
      };
    }

    case "migration_run": {
      const total = ctx.total || 0;
      const m = ctx.migrated || {};
      return {
        summary: `Tenant migration completed: ${total} legacy documents stamped with restaurant_id. SOPs: ${m.sops||0}, Chat: ${m.chat_sessions||0}, Receptionist: ${m.receptionist_sessions||0}.`,
        confidence: "user_action",
        rule: "Idempotent migration — safe to re-run. Only updates documents missing restaurant_id.",
        time: `${total} docs processed`,
      };
    }

    default:
      return {
        summary: entry.action || "System event logged to the Argus Council event bus.",
        confidence: "rule_based",
        rule: "SharedBrain.log_event() → supervisor_events + WebSocket broadcast",
        time: "< 1s",
      };
  }
}

// ── ROI Ticker ───────────────────────────────────────────────────────

function RoiTicker({ total, eventCount }) {
  return (
    <motion.div
      data-testid="roi-ticker"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-3 px-4 py-3 rounded-2xl"
      style={{
        background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.05))",
        border: "1px solid rgba(212,175,55,0.35)",
      }}
    >
      <TrendingUp className="w-5 h-5 text-[#D4AF37] shrink-0" />
      <div>
        <div className="flex items-baseline gap-1">
          <motion.span
            key={total}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl font-black text-[#D4AF37]"
          >
            ${total.toLocaleString()}
          </motion.span>
          <span className="text-xs text-gray-500">demo savings</span>
        </div>
        <p className="text-[9px] text-gray-600 mt-0.5">
          {eventCount} events · $5–15 per event handled
        </p>
      </div>
    </motion.div>
  );
}

// ── Confidence Dot ───────────────────────────────────────────────────

function ConfidenceDot({ level, showLabel = false }) {
  const c = CONFIDENCE[level] || CONFIDENCE.rule_based;
  return (
    <span
      title={`${c.label}: ${c.desc}`}
      className="inline-flex items-center gap-1 text-[10px] font-mono"
      style={{ color: c.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: c.color }} />
      {showLabel && c.label}
    </span>
  );
}

// ── Logic Summary Panel ──────────────────────────────────────────────

function LogicPanel({ logic, masked, gsMode }) {
  const transform = gsMode ? swapGoldenSpoon : masked ? (t) => t.replace(/\S+@\S+\.\S+/, "***@***.com") : t => t;
  const c = CONFIDENCE[logic.confidence] || CONFIDENCE.rule_based;

  return (
    <div className="px-4 pb-4 space-y-2">
      {/* Summary */}
      <div className="flex items-start gap-2 p-3 rounded-xl"
        style={{ background: c.color + "0d", border: `1px solid ${c.color}22` }}>
        <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: c.color }} />
        <div className="flex-1">
          <p className="text-xs font-medium" style={{ color: c.color }}>Why did this happen?</p>
          <p className="text-xs text-gray-300 mt-1 leading-relaxed">{transform(logic.summary)}</p>
        </div>
      </div>

      {/* Rule + Time row */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px]"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <ConfidenceDot level={logic.confidence} showLabel />
          <span className="text-gray-600">·</span>
          <span className="text-gray-400 font-mono">{logic.rule}</span>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px]"
          style={{ background: "rgba(0,255,65,0.05)", border: "1px solid rgba(0,255,65,0.15)", color: "#00FF41" }}>
          <Clock className="w-3 h-3" />
          <span className="font-mono">{logic.time}</span>
        </div>
      </div>
    </div>
  );
}

// ── Event type config ────────────────────────────────────────────────

const EVENT_CONFIG = {
  config_change:         { icon: Settings,     color: "#3b82f6", label: "Config",        bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.2)"  },
  maintenance_toggle:    { icon: Wrench,        color: "#f59e0b", label: "Maintenance",   bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.2)"  },
  supervisor_escalation: { icon: AlertTriangle, color: "#ef4444", label: "Escalation",    bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.2)"   },
  sop_change:            { icon: FileText,      color: "#8b5cf6", label: "SOP",           bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.2)"  },
  migration_run:         { icon: Activity,      color: "#00FF41", label: "Migration",     bg: "rgba(0,255,65,0.05)",    border: "rgba(0,255,65,0.15)"   },
  iris_task:             { icon: Zap,           color: "#D4AF37", label: "Iris Task",     bg: "rgba(212,175,55,0.08)",  border: "rgba(212,175,55,0.2)"  },
  supervisor_event:      { icon: Shield,        color: "#94a3b8", label: "Supervisor",    bg: "rgba(148,163,184,0.05)", border: "rgba(148,163,184,0.12)"},
};
const getEventConfig = t => EVENT_CONFIG[t] || EVENT_CONFIG.supervisor_event;

// ── Health Score widget — with Vitals Pulse ──────────────────────────

function HealthScore({ score, label, color, critical, high, total }) {
  const r = 28; const circ = 2 * Math.PI * r; const dash = (score / 100) * circ;
  return (
    <div data-testid="health-score" className="flex items-center gap-4 px-5 py-3 rounded-2xl"
      style={{ background: color + "10", border: `1px solid ${color}30` }}>
      <div className="relative flex items-center justify-center" style={{ width: 72, height: 72 }}>
        <svg width="72" height="72" style={{ transform: "rotate(-90deg)", overflow: "visible" }}>
          {/* Vitals pulse — two concentric rings that breathe in/out like a monitor */}
          <motion.circle cx="36" cy="36" r={r + 5} fill="none" stroke={color} strokeWidth="1.5"
            animate={{ opacity: [0.06, 0.45, 0.06] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
          <motion.circle cx="36" cy="36" r={r + 9} fill="none" stroke={color} strokeWidth="0.8"
            animate={{ opacity: [0, 0.18, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.4 }} />
          {/* Track ring */}
          <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
          {/* Score arc */}
          <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
            style={{ transition: "stroke-dasharray 1s ease" }} />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-lg font-black leading-none" style={{ color }}>{score}</span>
          <span className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">/ 100</span>
        </div>
      </div>
      <div>
        <p className="font-bold text-sm" style={{ color }}>{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">Last 24 hours</p>
        <div className="flex gap-3 mt-1.5">
          {critical > 0 && <span className="text-[10px] text-red-400">{critical} critical</span>}
          {high > 0 && <span className="text-[10px] text-amber-400">{high} high</span>}
          <span className="text-[10px] text-gray-600">{total} events</span>
        </div>
      </div>
    </div>
  );
}

// ── Audit Entry Row ──────────────────────────────────────────────────

function AuditEntry({ entry, masked, gsMode, isNew = false }) {
  const [expanded, setExpanded] = useState(false);
  const [showLogic, setShowLogic] = useState(false);

  const cfg   = getEventConfig(entry.type || entry.event_type);
  const Icon  = cfg.icon;
  const logic = generateLogicSummary(entry);

  const transform = gsMode ? swapGoldenSpoon
    : masked ? t => {
        let s = String(t);
        s = s.replace(/\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, "+1 (***) ***-****");
        s = s.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "***@***.com");
        return s;
      }
    : t => t;

  const displayCtx = gsMode ? swapContext(entry.context, true)
    : masked ? swapContext(entry.context, true)
    : entry.context;

  const time = new Date(entry.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const date = new Date(entry.timestamp).toLocaleDateString([], { month: "short", day: "numeric" });

  return (
    <motion.div
      data-testid={`audit-entry-${entry.audit_id || entry.event_id}`}
      initial={isNew ? { opacity: 0, y: -14, scale: 0.97 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="rounded-xl overflow-hidden"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Icon */}
        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: cfg.color + "20" }}>
          <Icon className="w-4 h-4" style={{ color: cfg.color }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: cfg.color + "20", color: cfg.color }}>
              {cfg.label}
            </span>
            <ConfidenceDot level={logic.confidence} />
            {entry.triggered_by && (
              <span className="text-[10px] text-gray-600 font-mono">{entry.triggered_by}</span>
            )}
          </div>
          <p className="text-xs text-white font-medium mt-0.5 truncate">
            {transform(entry.action)}
          </p>
        </div>

        {/* Timestamp */}
        <div className="shrink-0 text-right mr-1">
          <p className="text-[10px] text-gray-400 font-mono">{time}</p>
          <p className="text-[9px] text-gray-600">{date}</p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => { setShowLogic(l => !l); setExpanded(false); }}
            className="p-1.5 rounded-lg transition-colors"
            title="Why did this happen?"
            style={{
              background: showLogic ? logic.confidence === "deterministic" ? "rgba(0,255,65,0.12)" : logic.confidence === "user_action" ? "rgba(59,130,246,0.12)" : "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.05)",
              color: showLogic ? CONFIDENCE[logic.confidence]?.color : "#4b5563",
            }}
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
          {displayCtx && Object.keys(displayCtx).length > 0 && (
            <button
              onClick={() => { setExpanded(e => !e); setShowLogic(false); }}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-600 hover:text-gray-400 transition-colors"
              title="View raw context"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Logic Summary Panel */}
      <AnimatePresence>
        {showLogic && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            style={{ overflow: "hidden", borderTop: `1px solid ${cfg.border}` }}>
            <LogicPanel logic={logic} masked={masked} gsMode={gsMode} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Raw Context Panel */}
      <AnimatePresence>
        {expanded && displayCtx && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            style={{ overflow: "hidden", borderTop: `1px solid ${cfg.border}` }}>
            <div className="px-4 pb-3">
              <pre className="text-[10px] text-gray-500 font-mono mt-2 whitespace-pre-wrap leading-relaxed overflow-x-auto">
                {JSON.stringify(displayCtx, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main AuditTrailTab ────────────────────────────────────────────────

export default function AuditTrailTab({ token, wsLastEvent }) {
  const [entries,    setEntries]    = useState([]);
  const [health,     setHealth]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [isLive,     setIsLive]     = useState(true);
  const [masked,     setMasked]     = useState(false);
  const [gsMode,     setGsMode]     = useState(false);  // Golden Spoon Demo Mode
  const [filterType, setFilterType] = useState("all");
  const [filterDays, setFilterDays] = useState(7);
  const [simulating, setSimulating] = useState(false);
  const [pending,    setPending]    = useState([]);
  const [newIds,     setNewIds]     = useState(new Set());

  // ROI ticker — accumulated during this session when GS mode is active
  const [roiTotal,      setRoiTotal]      = useState(0);
  const [roiEventCount, setRoiEventCount] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  // ── Data fetching ──────────────────────────────────────────────────
  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: filterDays, limit: 100 };
      if (filterType !== "all") params.event_type = filterType;
      const res = await axios.get(`${API}/admin/audit-log`, { headers, params });
      setEntries(res.data.entries || []);
    } catch {}
    finally { setLoading(false); }
  }, [token, filterType, filterDays]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/health-score`, { headers });
      setHealth(res.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchAuditLog();
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, [fetchAuditLog, fetchHealth]);

  // ── WebSocket event handler ────────────────────────────────────────
  useEffect(() => {
    if (!wsLastEvent) return;
    const relevant = ["supervisor_event", "iris_task", "config_change", "supervisor_escalation"];
    if (!relevant.includes(wsLastEvent.type)) return;

    const liveEntry = {
      audit_id:    wsLastEvent.event_id || wsLastEvent.task_id || `ws-${Date.now()}`,
      event_type:  wsLastEvent.type,
      type:        wsLastEvent.type,
      action:      wsLastEvent.action || wsLastEvent.title || `${wsLastEvent.type} received`,
      context:     wsLastEvent,
      triggered_by: wsLastEvent.supervisor || "websocket",
      timestamp:   wsLastEvent.timestamp || new Date().toISOString(),
    };

    // Accumulate ROI when GS mode is active
    if (gsMode) {
      const val = DEMO_ROI[wsLastEvent.type] || 0;
      if (val > 0) {
        setRoiTotal(t => t + val);
        setRoiEventCount(n => n + 1);
      }
    }

    if (isLive) {
      setEntries(prev => [liveEntry, ...prev].slice(0, 150));
      setNewIds(ids => new Set([...ids, liveEntry.audit_id]));
      setTimeout(() => setNewIds(ids => { const n = new Set(ids); n.delete(liveEntry.audit_id); return n; }), 3000);
      fetchHealth();
    } else {
      setPending(prev => [liveEntry, ...prev]);
    }
  }, [wsLastEvent, isLive, gsMode]);

  const handleResume = () => {
    setIsLive(true);
    if (pending.length > 0) {
      setEntries(prev => [...pending, ...prev].slice(0, 150));
      setNewIds(new Set(pending.map(e => e.audit_id)));
      setTimeout(() => setNewIds(new Set()), 3000);
      setPending([]);
    }
  };

  // Reset ROI when GS mode is toggled off
  const toggleGsMode = () => {
    if (gsMode) { setRoiTotal(0); setRoiEventCount(0); }
    setGsMode(g => !g);
  };

  // ── Demo simulation ────────────────────────────────────────────────
  const handleSimulate = async () => {
    setSimulating(true);
    try { await axios.post(`${API}/admin/simulate-plating-issue`, null, { headers }); }
    catch {}
    finally { setSimulating(false); }
  };

  const filtered = filterType === "all"
    ? entries
    : entries.filter(e => (e.event_type || e.type) === filterType);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div data-testid="audit-trail-tab" className="space-y-5 max-w-4xl">

      {/* ── Header row: Health + ROI + Controls ── */}
      <div className="flex flex-wrap items-center gap-4">
        {health && (
          <HealthScore score={health.score} label={health.label} color={health.color}
            critical={health.critical_events_24h} high={health.high_events_24h}
            total={health.total_events_24h} />
        )}
        {gsMode && <RoiTicker total={roiTotal} eventCount={roiEventCount} />}

        <div className="flex flex-wrap gap-2 ml-auto">
          {/* Live / Pause */}
          <button data-testid="live-pause-toggle"
            onClick={isLive ? () => setIsLive(false) : handleResume}
            className="relative flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all"
            style={{
              background: isLive ? "rgba(0,255,65,0.1)" : "rgba(245,158,11,0.1)",
              border: `1px solid ${isLive ? "rgba(0,255,65,0.3)" : "rgba(245,158,11,0.3)"}`,
              color: isLive ? "#00FF41" : "#f59e0b",
            }}>
            {isLive
              ? <><span className="w-1.5 h-1.5 rounded-full bg-[#00FF41] animate-pulse" /> Live</>
              : <><Play className="w-3.5 h-3.5" /> Resume</>}
            {!isLive && pending.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-amber-500 text-black text-[8px] font-black flex items-center justify-center">
                {pending.length}
              </span>
            )}
          </button>

          {/* Golden Spoon Demo Mode */}
          <button data-testid="golden-spoon-toggle" onClick={toggleGsMode}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all"
            style={{
              background: gsMode ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${gsMode ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)"}`,
              color: gsMode ? "#D4AF37" : "#6b7280",
            }}>
            <Star className={`w-3.5 h-3.5 ${gsMode ? "fill-current" : ""}`} />
            {gsMode ? "The Golden Spoon" : "Demo Mode"}
          </button>

          {/* Simulate */}
          <button data-testid="simulate-plating-btn" onClick={handleSimulate} disabled={simulating}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all"
            style={{
              background: "rgba(212,175,55,0.08)",
              border: "1px solid rgba(212,175,55,0.25)",
              color: "#D4AF37",
              cursor: simulating ? "not-allowed" : "pointer",
            }}>
            {simulating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {simulating ? "Simulating..." : "Demo: Plating Issue"}
          </button>

          {/* Download Demo Report — only visible in Golden Spoon mode with events */}
          {gsMode && roiEventCount >= 1 && (
            <button
              data-testid="download-demo-report-btn"
              onClick={() => generateDemoReport({ health, roiTotal, roiEventCount, entries })}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all"
              style={{
                background: "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.08))",
                border: "1px solid rgba(212,175,55,0.5)",
                color: "#D4AF37",
                boxShadow: "0 2px 12px rgba(212,175,55,0.2)",
              }}
            >
              <Download className="w-3.5 h-3.5" />
              Download Report
            </button>
          )}
        </div>
      </div>

      {/* Golden Spoon active banner */}
      {gsMode && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between px-4 py-2.5 rounded-xl text-xs"
          style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.2)" }}>
          <span className="text-[#D4AF37] flex items-center gap-2">
            <Star className="w-3.5 h-3.5 fill-current" />
            <strong>Demo Mode — The Golden Spoon</strong> &mdash; All client data replaced with fictional restaurant persona. No real PII visible.
          </span>
          <span className="text-gray-600 text-[10px] font-mono">{GS.name} · {GS.cuisine}</span>
        </motion.div>
      )}

      {/* Confidence legend */}
      <div className="flex flex-wrap items-center gap-4 px-3 py-2 rounded-xl"
        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
        <span className="text-[10px] text-gray-600 uppercase tracking-wider">Confidence:</span>
        {Object.entries(CONFIDENCE).map(([key, c]) => (
          <span key={key} className="flex items-center gap-1.5 text-[10px]" style={{ color: c.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
        <span className="text-[10px] text-gray-700 ml-auto">Click <HelpCircle className="w-3 h-3 inline" /> on any event to see the reasoning</span>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-gray-600" />
        {[
          { v: "all",                   l: "All" },
          { v: "supervisor_escalation", l: "Escalations" },
          { v: "iris_task",             l: "Iris Tasks" },
          { v: "sop_change",            l: "SOPs" },
          { v: "config_change",         l: "Config" },
          { v: "maintenance_toggle",    l: "Maintenance" },
          { v: "supervisor_event",      l: "Supervisor" },
        ].map(({ v, l }) => {
          const cfg = v === "all" ? null : getEventConfig(v);
          const active = filterType === v;
          return (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setFilterType(v)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
              style={{
                background: active ? (cfg ? cfg.color + "20" : "rgba(255,255,255,0.1)") : "rgba(255,255,255,0.03)",
                border: `1px solid ${active ? (cfg ? cfg.border : "rgba(255,255,255,0.2)") : "rgba(255,255,255,0.06)"}`,
                color: active ? (cfg ? cfg.color : "#fff") : "#6b7280",
              }}>
              {l}
            </button>
          );
        })}
        <div className="ml-auto flex gap-1 items-center">
          {[1, 7, 30].map(d => (
            <button key={d} onClick={() => setFilterDays(d)}
              className="px-2 py-1 rounded text-[10px] font-mono transition-all"
              style={{
                background: filterDays === d ? "rgba(255,255,255,0.08)" : "transparent",
                color: filterDays === d ? "#fff" : "#6b7280",
                border: filterDays === d ? "1px solid rgba(255,255,255,0.12)" : "1px solid transparent",
              }}>{d}d</button>
          ))}
          <button onClick={() => { fetchAuditLog(); fetchHealth(); }}
            className="p-1 rounded text-gray-600 hover:text-gray-400 ml-1 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Paused banner */}
      {!isLive && (
        <div className="flex items-center justify-between px-4 py-2.5 rounded-xl text-xs"
          style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)" }}>
          <span className="text-amber-400 flex items-center gap-2">
            <Pause className="w-3.5 h-3.5" />
            Feed paused{pending.length > 0 && ` — ${pending.length} new event${pending.length !== 1 ? "s" : ""} queued`}
          </span>
          <button onClick={handleResume}
            className="px-3 py-1 rounded-lg text-xs bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 transition-colors">
            Resume Live
          </button>
        </div>
      )}

      {/* ── Timeline ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-600 text-sm">
          No events yet. Hit "Demo: Plating Issue" to trigger the full chain.
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence initial={false}>
            {filtered.map(entry => (
              <AuditEntry
                key={entry.audit_id || entry.event_id || entry.timestamp}
                entry={entry}
                masked={masked}
                gsMode={gsMode}
                isNew={newIds.has(entry.audit_id || entry.event_id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
